import React, { useEffect, useMemo, useState } from 'react';
import useGame from '../../contexts/useGame';
import useMediaQuery from '../../hooks/useMediaQuery';
import useFetch from '../../hooks/useFetch';
import {
  DehydratedRetailWarcraftTalentBuild,
  RetailWarcraftTalentTreeBlueprint,
} from '../../typings/viewModels/retailWarcraftTalentTreeBuildNode';
import useTalentTreeBlueprint, {
  useHydratedTalentTreeBuild,
} from '../../contexts/useTalentTreeBlueprint';
import { StandardTree } from './TalentTreeFullInternals';
import { HeroTree, HeroTreeProps } from './HeroTree';

type ExportCodeRankingParams = {
  kind: 'for-rankings';
  zoneId: number;
  rankingId: number;
  changeSetId: number | 'latest';
};

type ExportCodeSummaryParams = {
  kind: 'pre-loaded';
  exportCode: string;
};

type ExportCodeParams = ExportCodeRankingParams | ExportCodeSummaryParams;

export type TalentTreeFullProps = {
  dehydratedBuild: DehydratedRetailWarcraftTalentBuild;
  exportCodeParams: ExportCodeParams | null;
  defaultExpanded?: boolean;
  /**
   * Force the vertical layout. Does not adjust talent node sizes (as mobile does).
   */
  forceVerticalLayout?: boolean;
  isDisplayingPercent?: boolean;
  hasToggleButton?: boolean;
  isLatestRevision: boolean | null;
  /**
   * Enforces smaller icons, hides center elements
   */
  isCompact?: boolean | null;
  heroTreeOnly?: boolean;
  HeroTitle?: HeroTreeProps['HeroTitle'];
};

function useExportCode(params: ExportCodeParams | null): {
  exportCode?: string | null;
  loadExportCode: () => void;
} {
  const [{ data: exportCode, status }, doFetch] = useFetch<string>();

  let url: string | undefined = undefined;

  if (params && params.kind === 'for-rankings') {
    url = `/talent-tree/export-code/${params.kind}/${params.zoneId}/${params.rankingId}/${params.changeSetId}`;
  }

  const loadExportCode = useMemo(() => {
    if (url !== undefined) {
      // we need to capture the value of the `url` parameter *at this time* since it is technically mutable
      const fixedPoint = url;
      return () => doFetch(fixedPoint);
    } else {
      return () => {
        /* do nothing */
      };
    }
  }, [url, doFetch]);

  if (params?.kind === 'pre-loaded') {
    return {
      exportCode: params.exportCode,
      loadExportCode: () => {
        /* do nothing */
      },
    };
  }

  if (!params || status?.isError) {
    return {
      exportCode: null,
      loadExportCode: () => {
        /* do nothing */
      },
    };
  }

  return {
    exportCode,
    loadExportCode,
  };
}

export default function TalentTreeFull({
  exportCodeParams,
  defaultExpanded = false,
  forceVerticalLayout,
  isDisplayingPercent,
  hasToggleButton = true,
  isLatestRevision,
  dehydratedBuild,
  isCompact = false,
  heroTreeOnly = false,
  HeroTitle,
}: TalentTreeFullProps): JSX.Element {
  const blueprint = useTalentTreeBlueprint(dehydratedBuild.changeSet);
  const build = useHydratedTalentTreeBuild(
    dehydratedBuild,
    true,
    isDisplayingPercent
  );
  const [expanded, setExpanded] = useState(defaultExpanded);
  const minWidthLarge =
    useMediaQuery('(min-width: 1024px)') && !forceVerticalLayout;
  const minWidthHeroCompact =
    useMediaQuery('(min-width: 1400px)') && !forceVerticalLayout;

  const toggleExpanded = () => setExpanded((expanded) => !expanded);

  const { exportCode, loadExportCode } = useExportCode(exportCodeParams);

  useEffect(() => loadExportCode(), [loadExportCode]);

  if (!build || !blueprint) {
    return <div />;
  }

  const { classTree, specTree, heroTree } = build;
  const { labels } = blueprint;

  return (
    <>
      <div
        className={`talent-tree ${
          isDisplayingPercent ? 'talent-tree--wide' : ''
        } ${isCompact ? 'talent-tree--compact' : ''}`}
      >
        <div
          className={`talent-tree--container ${
            expanded ? '' : 'talent-tree--container--collapsed'
          }
           ${
             heroTreeOnly
               ? ''
               : forceVerticalLayout
               ? 'talent-tree--container--vertical-layout'
               : 'talent-tree--container--default-layout'
           }
          `}
        >
          {!heroTreeOnly && (
            <StandardTree
              {...classTree}
              expanded={expanded}
              kind='class'
              minWidthLarge={minWidthLarge}
              labels={labels}
              descriptionClassName={dehydratedBuild.changeSet.className}
              exportCode={exportCode}
              isDisplayingPercent={isDisplayingPercent}
              spellModifierId={blueprint.spellModifierId}
            />
          )}
          {heroTree ? (
            <HeroTree
              {...heroTree}
              icon={heroTree.artwork}
              compact={isCompact || !minWidthHeroCompact}
              isDisplayingPercent={isDisplayingPercent}
              spellModifierId={blueprint.spellModifierId}
              expanded={expanded}
              minWidthLarge={minWidthLarge}
              blueprint={blueprint}
              heroSpecId={dehydratedBuild.heroSpecId}
              descriptionClassName={dehydratedBuild.changeSet.className}
              heroTreeOnly={heroTreeOnly}
              HeroTitle={HeroTitle}
            />
          ) : (
            <div />
          )}
          {heroTreeOnly || (!expanded && !minWidthLarge) ? null : (
            <StandardTree
              {...specTree}
              expanded={expanded}
              kind='spec'
              minWidthLarge={minWidthLarge}
              labels={labels}
              descriptionClassName={dehydratedBuild.changeSet.className}
              exportCode={exportCode}
              isDisplayingPercent={isDisplayingPercent}
              spellModifierId={blueprint.spellModifierId}
            />
          )}
        </div>
        {isDisplayingPercent && isCompact ? null : (
          <DesktopInteractions
            labels={labels}
            toggleExpanded={toggleExpanded}
            exportCode={exportCode}
            expanded={expanded}
            isDisplayingPercent={isDisplayingPercent ?? false}
            hasToggleButton={hasToggleButton}
            isLatestRevision={isLatestRevision}
          />
        )}
      </div>
    </>
  );
}

type DesktopInteractionsProps = {
  labels: RetailWarcraftTalentTreeBlueprint['labels'];
  toggleExpanded: () => void;
  exportCode: string | undefined | null;
  expanded: boolean;
  isDisplayingPercent: boolean;
  hasToggleButton: boolean;
  isLatestRevision: TalentTreeFullProps['isLatestRevision'];
};

function DesktopInteractions({
  labels,
  toggleExpanded,
  exportCode,
  expanded,
  isDisplayingPercent,
  hasToggleButton,
  isLatestRevision,
}: DesktopInteractionsProps) {
  const game = useGame();
  const bail = !isDisplayingPercent && expanded && exportCode === null;
  const [revisionNoteActive, setRevisionNoteActive] = useState(
    isLatestRevision === false
  );

  if (bail) {
    return null;
  }

  return (
    <>
      <div
        className={`talent-tree__interactions-export ${
          isDisplayingPercent
            ? 'talent-tree__interactions-export--heat-map'
            : ''
        }`}
      >
        {exportCode !== null && !isDisplayingPercent ? (
          <>
            <CopyButton exportCode={exportCode}>{labels.export}</CopyButton>

            <ExternalTreeLink exportCode={exportCode}>
              {labels.edit}
            </ExternalTreeLink>
          </>
        ) : null}

        {isDisplayingPercent && game ? (
          revisionNoteActive ? (
            <div
              className={'talent-tree__interactions-export__outdated-revision'}
            >
              <div>
                <svg
                  className={
                    'talent-tree__interactions-export__outdated-revision--icon'
                  }
                  width='24'
                  height='20'
                  viewBox='0 0 24 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M11.0063 1.04976C11.4408 0.2972 12.527 0.2972 12.9615 1.04976L22.5979 17.7405C23.0324 18.4931 22.4893 19.4338 21.6203 19.4338H2.3475C1.47851 19.4338 0.935398 18.4931 1.36989 17.7405L11.0063 1.04976Z'
                    fill='#FFBA69'
                    stroke='#F06212'
                    strokeWidth='0.967581'
                  />
                  <g filter='url(#filter0_d_562_220374)'>
                    <path
                      d='M10.1797 5.59737H13.4679V8.12215L12.8329 13.1944H10.7995L10.1797 8.12215V5.59737ZM10.293 13.9654H13.3621V16.6792H10.293V13.9654Z'
                      fill='white'
                    />
                  </g>
                  <defs>
                    <filter
                      id='filter0_d_562_220374'
                      x='8.24446'
                      y='4.95236'
                      width='7.15859'
                      height='14.9521'
                      filterUnits='userSpaceOnUse'
                      colorInterpolationFilters='sRGB'
                    >
                      <feFlood floodOpacity='0' result='BackgroundImageFix' />
                      <feColorMatrix
                        in='SourceAlpha'
                        type='matrix'
                        values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                        result='hardAlpha'
                      />
                      <feOffset dy='1.29011' />
                      <feGaussianBlur stdDeviation='0.967581' />
                      <feComposite in2='hardAlpha' operator='out' />
                      <feColorMatrix
                        type='matrix'
                        values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0'
                      />
                      <feBlend
                        mode='normal'
                        in2='BackgroundImageFix'
                        result='effect1_dropShadow_562_220374'
                      />
                      <feBlend
                        mode='normal'
                        in='SourceGraphic'
                        in2='effect1_dropShadow_562_220374'
                        result='shape'
                      />
                    </filter>
                  </defs>
                </svg>
              </div>

              <div>
                <div
                  className={
                    'talent-tree__interactions-export__outdated-revision--title-container'
                  }
                >
                  <span
                    className={
                      'talent-tree__interactions-export__outdated-revision--title'
                    }
                  >
                    {labels.outdatedRevisionTitle}
                  </span>
                  <button
                    type='button'
                    onClick={() => {
                      setRevisionNoteActive(false);
                    }}
                    aria-label={labels.close}
                  >
                    X
                  </button>
                </div>
                <div
                  className={
                    'talent-tree__interactions-export__outdated-revision--subtitle'
                  }
                >
                  {labels.outdatedRevisionSubtitle}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div
                className={'talent-tree__interactions-export__percent-wrapper'}
              >
                <img
                  className={'talent-tree__interactions-export__percent-image'}
                  src={game.siteLogoImageUrl}
                  alt=''
                />
                <div>
                  <div
                    className={
                      'talent-tree__interactions-export__percent-title'
                    }
                  >
                    {labels.heatmapTitle}
                  </div>
                  <div
                    className={
                      'talent-tree__interactions-export__percent-subtitle'
                    }
                  >
                    {labels.heatmapSubtitle}
                  </div>
                </div>
              </div>
              <div
                className={
                  'talent-tree__interactions-export__percent-disclaimer'
                }
              >
                {labels.heatmapDisclaimer}
              </div>
            </>
          )
        ) : null}
      </div>
      {hasToggleButton ? (
        <div>
          <ShowMoreButton expanded={expanded} onClick={toggleExpanded}>
            {expanded ? labels.showLess : labels.showMore}
          </ShowMoreButton>
        </div>
      ) : null}
    </>
  );
}

type ShowMoreButtonProps = {
  children: string;
  onClick: () => void;
  expanded?: boolean;
};

function ShowMoreButton({ onClick, children, expanded }: ShowMoreButtonProps) {
  return (
    <button
      type='button'
      className={`talent-tree__interactions-show-more ${
        expanded ? 'talent-tree__interactions-show-more--expanded' : ''
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

type ExternalTreeLinkProps = {
  exportCode: string | undefined;
  children: string;
};

function ExternalTreeLink({ exportCode, children }: ExternalTreeLinkProps) {
  return (
    <a
      href={
        exportCode
          ? `https://www.wowhead.com/talent-calc/blizzard/${exportCode}`
          : undefined
      }
      target='_blank'
      rel='noreferrer'
      className='talent-tree__interactions-export__external-link'
    >
      <span>{children}</span>
      <i className='zmdi zmdi-edit' />
    </a>
  );
}

type CopyButtonProps = {
  exportCode: string | undefined;
  children: string;
};

function CopyButton({ exportCode, children }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => {
        setCopied(false);
      }, 1500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [copied]);

  const doCopy = useMemo(
    () =>
      exportCode
        ? () => {
            navigator.clipboard
              .writeText(exportCode)
              .then(() => {
                setCopied(true);
              })
              .catch(() => {
                const element = Object.assign(document.createElement('input'), {
                  value: exportCode,
                });

                document.body.append(element);
                element.select();
                element.setSelectionRange(0, 99999);

                try {
                  document.execCommand('copy');
                  setCopied(true);
                } catch {
                  alert('Copying failed.');
                } finally {
                  element.remove();
                }
              });
          }
        : undefined,
    [exportCode]
  );

  return (
    <button
      className={`talent-tree__interactions-export__copy-button ${
        copied ? 'talent-tree__interactions-export__copy-button--copied' : ''
      }`}
      onClick={doCopy}
    >
      <span>{children}</span>
      <i className={`zmdi zmdi-${copied ? 'check' : 'copy'}`} />
    </button>
  );
}
